import React from "react";
import { FormattedMessage } from "react-intl";
import Tags, { Tag } from "components/Tags";
import { ICON_TYPE } from "components/Icon";
import { HourlyRateRange } from "components/Profile";
import { shortenText } from "utils";

const JOB_DESCRIPTION_TAB = {
  upload: 0,
  review: 1,
};

const BLUEPRINT_TAB = {
  choose: 0,
  customize: 1,
};

/**
 * Steps of the Talent Simple role builder, in order. `key` is what gets persisted on the
 * role so reopening a draft returns to the step the customer left off on.
 */
const ROLE_BUILDER_STEPS = [
  {
    key: "JOB_DESCRIPTION",
    titleId: "Missions.Roles.TalentSimple.RoleBuilder.Steps.JobDescription",
    descriptionId: "Missions.Roles.TalentSimple.RoleBuilder.Description",
    tabTitleIds: [
      "Missions.Roles.TalentSimple.RoleBuilder.JobDescription.Tabs.Upload",
      "Missions.Roles.TalentSimple.RoleBuilder.JobDescription.Tabs.Review",
    ],
  },
  {
    key: "INFORMATION",
    titleId: "Missions.Roles.TalentSimple.RoleBuilder.Steps.Information",
    descriptionId: "Missions.Roles.TalentSimple.RoleBuilder.Information.Description",
  },
  {
    key: "BLUEPRINT",
    titleId: "Missions.Roles.TalentSimple.RoleBuilder.Steps.Blueprint",
    descriptionId: "Missions.Roles.TalentSimple.RoleBuilder.Blueprint.Description",
    tabTitleIds: [
      "Missions.Roles.TalentSimple.RoleBuilder.Blueprint.Tabs.Choose",
      "Missions.Roles.TalentSimple.RoleBuilder.Blueprint.Tabs.Customize",
    ],
  },
  {
    key: "REVIEW",
    titleId: "Missions.Roles.TalentSimple.RoleBuilder.Steps.Review",
    descriptionId: "Missions.Roles.TalentSimple.RoleBuilder.Review.Description",
  },
];

const ROLE_BUILDER_STEP = ROLE_BUILDER_STEPS.reduce((steps, { key }) => ({ ...steps, [key]: key }), {});

const CLEARED_JOB_DESCRIPTION_UPLOAD_VALUES = {
  companyName: undefined,
  companyUrl: undefined,
  jobDescription: undefined,
  jobDescriptionFile: undefined,
};

const ROLE_BUILDER_CONFIG = {
  JOB_DESCRIPTION_ROWS: 10,
  SUMMARY_DESCRIPTION_LENGTH: 120,
  BLUEPRINT_COLUMNS: 2,
  BLUEPRINT_TAGS_LIMIT: 3,
};

const getBlueprintVettingRows = (template) => [
  {
    id: "Missions.Roles.TalentSimple.RoleBuilder.Blueprint.Card.Requirements",
    icon: ICON_TYPE.listBullet,
    count: template?.requirementsCount,
  },
  {
    id: "Missions.Roles.TalentSimple.RoleBuilder.Blueprint.Card.Questions",
    icon: ICON_TYPE.message,
    count: template?.questionsCount,
  },
  {
    id: "Missions.Roles.TalentSimple.RoleBuilder.Blueprint.Card.InterviewQuestions",
    icon: ICON_TYPE.video,
    count: template?.interviewQuestionsCount,
  },
  {
    id: "Missions.Roles.TalentSimple.RoleBuilder.Blueprint.Card.Challenges",
    icon: ICON_TYPE.code,
    count: template?.challengesCount,
  },
];

const BLUEPRINT_CARD_CHECKS = [
  "Missions.Roles.TalentSimple.RoleBuilder.Blueprint.Card.IdVerification",
  "Missions.Roles.TalentSimple.RoleBuilder.Blueprint.Card.BackgroundCheck",
];

const getRoleBuilderStepIndex = (key) => ROLE_BUILDER_STEPS.findIndex((step) => step.key === key);

/**
 * The step with the given key, or the one `offset` places away from it.
 */
const getRoleBuilderStep = (key, offset = 0) => ROLE_BUILDER_STEPS[getRoleBuilderStepIndex(key) + offset];

const getRoleBuilderSteps = (activeStep) => {
  const activeIndex = getRoleBuilderStepIndex(activeStep);

  return ROLE_BUILDER_STEPS.map(({ titleId }, index) => ({
    label: titleId,
    isComplete: index < activeIndex,
    isActive: index === activeIndex,
  }));
};

/**
 * What the summary shows, which grows as the builder captures more of the role.
 */
const getRoleBuilderSummary = (activeStep, { companyName, companyUrl, role } = {}) => {
  if (activeStep === ROLE_BUILDER_STEP.JOB_DESCRIPTION) {
    return [
      {
        title: <FormattedMessage id="AIRoleBuilder.Form.CompanyName.Label" />,
        body: companyName,
      },
      {
        title: <FormattedMessage id="AIRoleBuilder.Form.CompanyURL.Label" />,
        body: companyUrl,
      },
    ];
  }

  const { rate, currency, frequency } = role?.customerBudget || {};

  return [
    {
      title: <FormattedMessage id="Missions.Roles.TalentSimple.RoleBuilder.Summary.RoleName" />,
      body: role?.name,
    },
    {
      title: <FormattedMessage id="Missions.Roles.TalentSimple.RoleBuilder.Summary.RoleDescription" />,
      body: shortenText(role?.description, ROLE_BUILDER_CONFIG.SUMMARY_DESCRIPTION_LENGTH),
    },
    ...tagsRow("Missions.Roles.TalentSimple.RoleBuilder.Summary.Disciplines", role?.disciplines),
    ...tagsRow("Missions.Roles.TalentSimple.RoleBuilder.Summary.Skills", role?.primarySkills),
    ...summaryRow(
      "Missions.Roles.TalentSimple.RoleBuilder.Summary.EngagementTime",
      role?.engagementTime && (
        <FormattedMessage
          id="Missions.Roles.TalentSimple.RoleBuilder.Summary.HoursPerWeek"
          values={{ hours: role.engagementTime }}
        />
      )
    ),
    ...summaryRow(
      "Missions.Roles.TalentSimple.RoleBuilder.Summary.Rate",
      rate?.min && rate?.max && (
        <Tag>
          <HourlyRateRange currency={currency} low={rate.min} high={rate.max} frequency={frequency} />
        </Tag>
      )
    ),
    ...summaryRow("Global.Timezone", role?.timezone?.name),
  ];
};

const tagsRow = (id, items) => summaryRow(id, items?.length > 0 && <Tags data={items} />);

/**
 * Supplementary summary rows, which are left out entirely until the role carries a value.
 */
const summaryRow = (id, body) => (body ? [{ title: <FormattedMessage id={id} />, body }] : []);

export {
  BLUEPRINT_CARD_CHECKS,
  BLUEPRINT_TAB,
  CLEARED_JOB_DESCRIPTION_UPLOAD_VALUES,
  JOB_DESCRIPTION_TAB,
  ROLE_BUILDER_CONFIG,
  ROLE_BUILDER_STEP,
  getBlueprintVettingRows,
  getRoleBuilderStep,
  getRoleBuilderStepIndex,
  getRoleBuilderSteps,
  getRoleBuilderSummary,
};

export default ROLE_BUILDER_STEPS;
